import { useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BrandStrip } from "../components/BrandStrip";
import { EmptyState } from "../components/EmptyState";
import {
  FacilityFiltros,
  FiltrosSinCoincidencias,
  type FacilityChip,
} from "../components/facility/FacilityFiltros";
import { Icon } from "../components/icons";
import { QueryBoundary } from "../components/QueryBoundary";
import { Skel } from "../components/Skel";
import { SkelTable } from "../components/skeleton/SkelTable";
// TODO(#dashboard-cleanup): re-enable when trend data lands — Spark unused
// import { Spark } from "../components/icons";
import { Pager } from "../components/Pager";
import { StatusPill } from "../components/StatusPill";
import { useFacilityDashboard, useRecipe } from "../lib/api/queries";
import {
  clampPage,
  hasActiveFilters,
  normalizePagingParams,
  optionsFor,
  PAGE_SIZE,
  pageSlice,
  resultados,
  totalPages as countPages,
} from "../lib/facility/listState";
import {
  applyFilters,
  buildFilas,
  CLEAR_FILTERS,
  ESTADOS_TIENDA,
  FILTER_KEYS,
  LIST_CONFIG,
  parseListState,
  REPORTE_LABEL,
  REPORTES,
  valueOf,
  writeListState,
  type EnrichedAsset,
  type FilterKey,
  type Reporte,
  type TiendasPatch,
  type TiendasState,
} from "../lib/facility/tiendasList";
import { useListAnnouncer } from "../lib/facility/useListAnnouncer";
import {
  daysSince,
  fmtDate,
  statusLabel,
  storeSubtitle,
  storeTitle,
  type StoreStatus,
} from "../lib/util";

// Filters, search and page live in the URL (lib/facility/tiendasList.ts):
// `?zona=`, `?estado=`, `?reporte=`, `?marca=`, `?q=`, `?pagina=`. The
// overview's coverage tile links here with `?reporte=hasta30`.
export function Tiendas() {
  const dashboard = useFacilityDashboard();
  const { data } = dashboard;
  const { data: recipe } = useRecipe();
  const idleAfterDays = recipe?.alDiaThresholdDays;
  const [searchParams, setSearchParams] = useSearchParams();
  const [live, announce] = useListAnnouncer();

  // Zones are the facility asset groups. byAssetGroup carries every zona
  // the caller can see (the whole org for org-scoped roles, only their own
  // for scoped ones), so a single-zone org — or a caller confined to one
  // zone — gets no Zona column or filter: one value everywhere says nothing.
  // A `?zona=` in the URL is then ignored too.
  const showZones = (data?.byAssetGroup.length ?? 0) > 1;
  const effective = (s: TiendasState): TiendasState => (showZones ? s : { ...s, zona: [] });
  const state = effective(parseListState(searchParams));

  const zoneNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of data?.byAssetGroup ?? []) m.set(g.id, g.name);
    return m;
  }, [data]);

  // «Hoy» is fixed per dashboard load: the 30-day window is recomputed on
  // refresh, not on every render.
  const now = useMemo(() => Date.now(), [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const filas = useMemo(
    () =>
      buildFilas(data?.byAsset ?? [], data?.byAssetGroup ?? [], idleAfterDays, now).sort((a, b) =>
        // Most observations first, then by the displayed name (founder
        // decision 2026-10-22).
        b.t.openFindingsCount !== a.t.openFindingsCount
          ? b.t.openFindingsCount - a.t.openFindingsCount
          : storeTitle(a.t).localeCompare(storeTitle(b.t), "es")
      ),
    [data, idleAfterDays, now]
  );

  const filtered = applyFilters(filas, state);
  const pages = countPages(filtered.length);
  const pagina = clampPage(state.pagina, filtered.length);
  const pageRows = pageSlice(filtered, pagina);

  function update(patch: TiendasPatch) {
    setSearchParams((prev) => writeListState(prev, patch), { replace: true });
  }
  /** Changes filters and announces the result, computed on the next state
   *  rather than this render's. */
  function patchAndAnnounce(patch: TiendasPatch) {
    const next = effective(parseListState(writeListState(searchParams, patch)));
    update(patch);
    announce(resultados(applyFilters(filas, next).length));
  }

  // A URL that is not the canonical one for what is painted (page out of
  // range, `?pagina=abc`, a legacy `?tiendas_page=`) is corrected in place.
  const ready = data !== undefined;
  useEffect(() => {
    if (!ready) return;
    const canon = normalizePagingParams(searchParams, pagina, LIST_CONFIG);
    if (canon) setSearchParams(canon, { replace: true });
  }, [ready, pagina, searchParams, setSearchParams]);

  const brandOptions = useMemo(() => {
    const map = new Map<
      string,
      { id: string; name: string; count: number; logoUrl: string | null }
    >();
    for (const { t: a } of filas) {
      if (!a.brandId || !a.brandName) continue;
      const entry = map.get(a.brandId);
      if (entry) entry.count += 1;
      else
        map.set(a.brandId, {
          id: a.brandId,
          name: a.brandName,
          count: 1,
          logoUrl: a.brandLogoUrl,
        });
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, "es"));
  }, [filas]);

  const chip = (
    key: FilterKey,
    label: string,
    labelOf: (v: string) => string,
    domain?: readonly string[]
  ): FacilityChip => ({
    key,
    label,
    options: optionsFor(filas, state, key, FILTER_KEYS, valueOf, {
      labelOf,
      ...(domain ? { domain } : {}),
    }),
    value: state[key],
  });
  const chips: FacilityChip[] = [
    ...(showZones ? [chip("zona", "Zona", (v) => zoneNameById.get(v) ?? "Zona seleccionada")] : []),
    chip("estado", "Estado", (v) => statusLabel(v as StoreStatus), ESTADOS_TIENDA),
    chip("reporte", "Último reporte", (v) => REPORTE_LABEL[v as Reporte], REPORTES),
  ];
  const filtrosActivos = hasActiveFilters(state, FILTER_KEYS);

  // The header lands at once on every branch; only the body below it
  // skeletonizes, errors or fills (ADR-0166). The eyebrow adds the count
  // once data is there; the static «Tus tiendas» already holds the line box,
  // so the header never shows a loading state of its own (Proyectos idiom).
  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__lead">
          <div className="page-header__eyebrow">
            Tus tiendas{data ? ` · ${data.totals.assets}` : ""}
          </div>
          <h1 className="page-header__title">Tiendas</h1>
          <p className="page-header__sub">
            Estado semáforo de cada tienda, score de reportes y observaciones abiertas. Click en una
            fila para ver detalle e historial.
          </p>
        </div>
      </div>

      <QueryBoundary query={dashboard} label="tus tiendas" skeleton={<TiendasSkeleton />}>
        {() => (
          <>
            <BrandStrip
              brands={brandOptions}
              value={state.marca[0] ?? "all"}
              onChange={(id) => patchAndAnnounce({ marca: id === "all" ? [] : [id] })}
              countLabel="tiendas"
              totalCount={filas.length}
            />

            <div className="panel">
              <FacilityFiltros
                searchPlaceholder="Buscar tienda, zona o marca…"
                q={state.q}
                onSearch={(q) => patchAndAnnounce({ q })}
                chips={chips}
                onChip={(key, next) => patchAndAnnounce({ [key as FilterKey]: next })}
                active={filtrosActivos}
                onClear={() => patchAndAnnounce(CLEAR_FILTERS)}
                resultCount={filtered.length}
                live={live}
              />

              <div className="panel__body--flush">
                {filtered.length === 0 ? (
                  filtrosActivos ? (
                    <FiltrosSinCoincidencias
                      noun="tienda"
                      onClear={() => patchAndAnnounce(CLEAR_FILTERS)}
                    />
                  ) : (
                    <EmptyState
                      title="Sin tiendas para mostrar"
                      description="Cuando tengas tiendas registradas, aparecen aquí."
                    />
                  )
                ) : (
                  <table className="dtable">
                    <caption className="sr-only">Listado de tiendas</caption>
                    <thead>
                      <tr>
                        <th scope="col">Tienda</th>
                        {showZones && <th scope="col">Zona</th>}
                        <th scope="col" className="col-num">
                          Score
                        </th>
                        <th scope="col" className="col-num">
                          Observaciones abiertas
                        </th>
                        <th scope="col">Último reporte</th>
                        {/* TODO(#dashboard-cleanup): re-enable when trend data lands */}
                        {/* <th scope="col">Tendencia</th> */}
                        <th scope="col">Estado</th>
                        <th scope="col"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageRows.map((f) => (
                        <TiendaRow key={f.id} t={f.t} showZone={showZones} zoneName={f.zona} />
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {filtered.length > 0 && (
                <Pager
                  currentPage={pagina}
                  totalPages={pages}
                  totalItems={filtered.length}
                  pageSize={PAGE_SIZE}
                  onPage={(n) => {
                    update({ pagina: n });
                    announce(`Página ${n} de ${pages}`);
                  }}
                />
              )}
            </div>
          </>
        )}
      </QueryBoundary>
    </div>
  );
}

// The real panel and table: the filter bar's frame with blocks where the
// search box and dropdowns go, then the stores table with its static
// headers and enough rows to fill a 1280×800 viewport. No BrandStrip —
// it only renders with two or more brands, unknown until the data lands.
// No Zona column or dropdown either: they only render with two or more
// zones, which is also unknown until the data lands.
function TiendasSkeleton() {
  const headers = ["Tienda", "Score", "Observaciones abiertas", "Último reporte", "Estado", ""];
  return (
    <div className="panel">
      <div className="filters" aria-hidden="true">
        <Skel width={280} height={30} />
        <Skel width={110} height={30} />
        <div className="filter-spacer" />
        <Skel variant="text" width={90} />
      </div>
      <SkelTable rows={8} cols={headers.length} headers={headers} />
    </div>
  );
}

function TiendaRow({
  t,
  showZone,
  zoneName,
}: {
  t: EnrichedAsset;
  showZone: boolean;
  zoneName: string | null | undefined;
}) {
  const navigate = useNavigate();
  const ago = daysSince(t.lastVisit);
  // TODO(#dashboard-cleanup): re-enable when trend data lands
  // trend comes from the API most-recent-first; reverse so the
  // sparkline reads left-to-right chronologically. The tone is
  // "crit" when the most recent score is below the oldest in the
  // window — i.e. score is trending down.
  const chronoTrend = [...t.trend].reverse();
  const trendTone =
    chronoTrend.length >= 2 && chronoTrend[chronoTrend.length - 1]! < chronoTrend[0]!
      ? "crit"
      : "ok";
  void chronoTrend;
  void trendTone;

  function handleRowClick(e: React.MouseEvent<HTMLTableRowElement>) {
    // Real interactive children (the address Link, the chevron Link,
    // any buttons) handle their own activation. The row-onClick is a
    // mouse-affordance enhancement only — keyboard users still tab
    // to the cell-level <Link>.
    if ((e.target as HTMLElement).closest('a, button, [role="button"]')) return;
    navigate(`/tiendas/${t.id}`);
  }

  return (
    <tr className="row-link" onClick={handleRowClick}>
      <td>
        <div className="cell-with-sub">
          <Link to={`/tiendas/${t.id}`} className="cell-strong cell-strong--link">
            {storeTitle(t)}
          </Link>
          <span className="cell-sub">
            {[storeSubtitle(t), t.brandName].filter(Boolean).join(" · ") || "—"}
          </span>
        </div>
      </td>
      {showZone && <td>{zoneName ?? "—"}</td>}
      <td className="col-num cell-strong">
        {t.score !== null ? `${t.score.toFixed(2)}/5.0` : "—"}
      </td>
      <td className="col-num">{t.openFindingsCount}</td>
      <td className="cell-meta">
        {fmtDate(t.lastVisit)}
        {ago !== null && ` · ${ago}d`}
      </td>
      {/* TODO(#dashboard-cleanup): re-enable when trend data lands */}
      {/* <td>
        {chronoTrend.length >= 2 ? (
          <Spark data={chronoTrend} tone={trendTone} w={64} h={20} />
        ) : (
          <span className="cell-sub">—</span>
        )}
      </td> */}
      <td>
        <StatusPill status={t.status} />
      </td>
      <td>
        <Link
          to={`/tiendas/${t.id}`}
          className="row-chevron"
          aria-label={`Abrir detalle de ${storeTitle(t, "esta tienda")}`}
        >
          <Icon.ChevronRight />
        </Link>
      </td>
    </tr>
  );
}
